<script setup lang="ts">
import {EVENT_TRACKING} from '~/constants/wadConstants'

definePageMeta({
  middleware: ['video-details']
})
const config = useRuntimeConfig()
const {isAuthenticated, isLoading} = useAuthUser()

const {trackEvent} = useTracking()
const {protectedVideoLoginRedirectUrl, videoDetailsPageUrl, protectedVideoSignupRedirectUrl} = useDeveloperSignup()
const startedPlaying = ref(false)
const video = useState<Video>('currentVideo')
const videoRelatedJobs = ref<Array<JobLight>>([])
const videoRecommendedJobs = ref<Array<JobLight>>([])
const videoRecommendedVideos = ref<Array<VideoLight>>([])
const videoRecommendedMoments = ref<Array<VideoMoment>>([])

const {$api} = useNuxtApp();
const response = await $api<VideoRecommendations>(`/v2/videos/${video.value.id}/recommendations`)
videoRelatedJobs.value = response.related_jobs ?? []
videoRecommendedJobs.value = response.job_recommendations ?? []
videoRecommendedVideos.value = response.video_recommendations ?? []
videoRecommendedMoments.value = response.moments_recommendations ?? []

const released = computed(() => {
  if (!video.value?.release_date) {
    return video.value?.event_name ?? '';
  }
  const formattedDate = new Date(video.value?.release_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return video.value?.event_name ? `${formattedDate} • ${video.value?.event_name}` : formattedDate;
});
const requiresLogin = computed(() => {
  return video.value?.requires_login_status && !isAuthenticated.value && !isLoading.value;
});
const videoPlaceholderImage = computed(() => video.value?.img_url.replace('_640x360', '_760x427?q=75'))

//region Tracking

const trackingData = computed(() => ({video_id: video.value?.id}));

onMounted( () => {
  trackEvent(EVENT_TRACKING.VIDEO_PAGE_VIEW, trackingData.value);
});
function trackPlayInteraction() {
  if (!startedPlaying.value) {
    startedPlaying.value = true
    trackEvent(EVENT_TRACKING.VIDEO_STARTED, trackingData.value)
  }
}

//endregion

useHead(() => {
  const v = video.value
  const title = video.value?.speaker_name ? `${video.value?.speaker_name} - ${video.value?.title}` : video.value?.title;
  return {
    title: title,
    meta: [
      {hid: 'description', name: 'description', content: v?.teaser || ''},
      {hid: 'og:description', property: 'og:description', content: v?.teaser || ''},
      {hid: 'og:title', property: 'og:title', content: title},
      {hid: 'og:type', property: 'og:type', content: 'article'},
      {hid: 'og:image', property: 'og:image', content: v?.img_url || ''},
      {hid: 'og:url', property: 'og:url', content: videoDetailsPageUrl(v)},
      {hid: 'twitter:url', property: 'twitter:url', content: videoDetailsPageUrl(v)},
      {hid: 'twitter:image', property: 'twitter:image', content: v?.img_url || ''},
      {hid: 'twitter:description', property: 'twitter:description', content: v?.teaser || ''}
    ],
  }
})

const videoPlayerRef = ref<InstanceType<typeof VideoPlayer> | null>(null);

//region TOC

const selectedIndex = ref<number | null>(null);
function seekToTimestamp(timestamp: number, index: number) {
  selectedIndex.value = index;
  videoPlayerRef.value?.seekTo(timestamp / 1000);
}

const playback = reactive({
  currentTime: 0
});

function handleTimeUpdate(time: number) {
  playback.currentTime = time;
}

const activeIndex = computed(() => {
  if (!video.value?.sections) return null;
  return video.value.sections.findIndex((section, i, arr) => {
    const nextStart = arr[i + 1]?.start_ms ?? Infinity;
    return playback.currentTime * 1000 >= section.start_ms &&
        playback.currentTime * 1000 < nextStart;
  });
});

const highlightedIndex = computed(() => {
  return selectedIndex.value !== null ? selectedIndex.value : activeIndex.value;
});

watch(activeIndex, (newIndex, oldIndex) => {
  if (newIndex !== oldIndex && newIndex !== null) {
    selectedIndex.value = newIndex;
  }
});

//endregion

//region AI Summary

const summaryOpen = ref(false)
const summaryViewed = ref(false)
const typedSummary = ref<string[]>([]);
const typingInProgress = ref<boolean>(false);
const summaryFullyTyped = ref<boolean>(false);
const summaryRef = ref<HTMLElement | null>(null);
const showSummaryOverlay = ref<boolean>(false);

const toggleSummary = () => {
  if (!summaryViewed.value) {
    trackEvent(EVENT_TRACKING.VIDEO_SUMMARY_VIEW, {video_id: video.value?.id})
    summaryViewed.value = true;
  }
  summaryOpen.value = !summaryOpen.value;
  if (summaryOpen.value) {
    startTyping();
  }
}

const startTyping = async () => {
  if (typingInProgress.value || summaryFullyTyped.value) {
    return;
  }
  typingInProgress.value = true;
  typedSummary.value = [];
  if (video.value.summary) {
    for (const block of video.value.summary) {
      typedSummary.value.push('');
      for (let i = 0; i < block.length; i++) {
        typedSummary.value[typedSummary.value.length - 1] = block.slice(0, i + 1);
        await new Promise(resolve => setTimeout(resolve, 10));
      }
    }
  }
  summaryFullyTyped.value = true;
  typingInProgress.value = false;
  handleSummaryScroll();
};

const handleKeydown = (e: KeyboardEvent) => {
  if (!summaryRef.value) return

  const el = summaryRef.value
  const scrollAmount = 15

  switch (e.key) {
    case 'ArrowUp':
      el.scrollTop -= scrollAmount
      e.preventDefault()
      break
    case 'ArrowDown':
      el.scrollTop += scrollAmount
      e.preventDefault()
      break
    case 'PageUp':
      el.scrollTop -= el.clientHeight
      e.preventDefault()
      break
    case 'PageDown':
      el.scrollTop += el.clientHeight
      e.preventDefault()
      break
    case ' ':
      el.scrollTop += e.shiftKey ? -el.clientHeight : el.clientHeight
      e.preventDefault()
      break
  }
}

const handleSummaryScroll = () => {
  if(typingInProgress.value) {
    showSummaryOverlay.value = true;
    return;
  }

  const el = summaryRef.value
  if (!el) return

  const overflowing = el.scrollHeight > el.clientHeight
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 60

  showSummaryOverlay.value = overflowing && !atBottom
}

watch(summaryRef, (el) => {
    if (!el) {
      return;
    }
    el.addEventListener('scroll', handleSummaryScroll);
    handleSummaryScroll();
  }, { immediate: true })

onBeforeUnmount(() => {
  summaryRef.value?.removeEventListener('scroll', handleSummaryScroll)
})

//endregion

//region Video Player

const {getVideoChaptersBlobUrl} = useVideo();
const chaptersBlobUrl = computed(() => {
  return getVideoChaptersBlobUrl(video.value);
});
const captionsUrl = computed(() => {
  return video.value?.id ? `${config.public.wadApiBaseUrl}/v2/videos/${video.value.id}/subtitle.vtt` : null;
})

//endregion

</script>

<template>
  <div class="wad4__page wad4__page--spaced wad4-video-details" id="main-content" tabindex="0">
    <main v-if="video" class="wad4__container wad4__container--columns wad4-video-details__main">
      <header class="wad4-video-details__main-header">
        <div class="wad4-video-details__main-header-left">
          <div class="wad4-video-details__info">
            <div class="wad4-video-details__info-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M17.725 17.6258H12.05L9.225 20.4508C8.84167 20.8341 8.37067 21.0258 7.812 21.0258C7.254 21.0258 6.78333 20.8341 6.4 20.4508L3.575 17.6258C3.19167 17.2425 3 16.7675 3 16.2008C3 15.6341 3.19167 15.1591 3.575 14.7758L6.375 11.9758V6.30078L17.725 17.6258ZM12.9 15.6258L8.375 11.1008V12.8008L4.975 16.2008L7.8 19.0258L11.2 15.6258H12.9ZM7.225 4.37578C9.00833 3.24245 10.9373 2.78812 13.012 3.01278C15.0873 3.23812 16.875 4.10078 18.375 5.60078C19.875 7.10078 20.7373 8.88812 20.962 10.9628C21.1873 13.0381 20.7333 14.9675 19.6 16.7508L18.15 15.3008C18.9 13.9341 19.1627 12.4881 18.938 10.9628C18.7127 9.43812 18.05 8.12578 16.95 7.02578C15.85 5.92578 14.5377 5.26312 13.013 5.03778C11.4877 4.81312 10.0417 5.07578 8.675 5.82578L7.225 4.37578ZM10.175 7.32578C11.125 7.04245 12.0833 6.98412 13.05 7.15078C14.0167 7.31745 14.85 7.75078 15.55 8.45078C16.25 9.15078 16.679 9.98012 16.837 10.9388C16.9957 11.8968 16.9333 12.8508 16.65 13.8008L14.95 12.1008C14.95 11.6841 14.8877 11.2798 14.763 10.8878C14.6377 10.4965 14.4333 10.1591 14.15 9.87578C13.85 9.57578 13.5043 9.35912 13.113 9.22578C12.721 9.09245 12.3083 9.02578 11.875 9.02578L10.175 7.32578Z" fill="currentColor"/>
              </svg>
            </div>
            <div class="wad4-video-details__info-data">
              <h3 class="wad4-video-details__info-speaker">{{ video.speaker_name }}</h3>
              <time class="wad4-video-details__info-release">{{ released }}</time>
            </div>
          </div>
          <h1 class="wad4-video-details__title">{{ video.title }}</h1>
          <p class="wad4-video-details__teaser">{{ video.teaser }}</p>
        </div>
        <div class="wad4-video-details__info__right">
          <button tabindex="0" v-if="video.summary?.length" class="wad4-button-secondary" @pointerup="toggleSummary">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M10 19L7.5 13.5L2 11L7.5 8.5L10 3L12.5 8.5L18 11L12.5 13.5L10 19ZM18 21L16.75 18.25L14 17L16.75 15.75L18 13L19.25 15.75L22 17L19.25 18.25L18 21Z" fill="currentColor"/>
            </svg>
            <span class="wad3-button__text">AI Quick Summary</span>
            <svg v-if="summaryOpen" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 8.29492L6 14.2949L7.41 15.7049L12 11.1249L16.59 15.7049L18 14.2949L12 8.29492Z" fill="currentColor"/>
            </svg>
            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.59 8.29492L12 12.8749L7.41 8.29492L6 9.70492L12 15.7049L18 9.70492L16.59 8.29492Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </header>
      <div class="wad4-video-details__media-row">
        <div class="wad4-video-details__media-main">
          <template v-if="video.summary">
            <transition name="summary-fade-slide">
              <div v-if="summaryOpen"  class="wad4-video-details__summary-container">
                <div class="wad4-video-details__summary-outer">
                  <div class="wad4-video-details__summary-inner">
                    <div class="wad4-video-details__summary" ref="summaryRef" tabindex="0" @keydown="handleKeydown">
                      <p v-for="(block, i) in typedSummary" :key="i" v-html="block"></p>
                    </div>
                  </div>
                  <div v-if="showSummaryOverlay" class="wad4-video-details__summary-gradient-overlay"/>
                </div>
              </div>
            </transition>
          </template>
          <article class="wad4-video-details__player">
            <img class="wad4-video-details__player-image" fetchpriority="high" :src="videoPlaceholderImage" width="16" height="9" :alt="video.title"/>
            <client-only>
              <div v-if="requiresLogin" class="wad4-video-details__player-overlay">
                <svg width="41" height="40" viewBox="0 0 41 40" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.5" width="40" height="40" rx="20" fill="#F1F3FF"/>
                  <path d="M26.5 16H25.5V14C25.5 11.24 23.26 9 20.5 9C17.74 9 15.5 11.24 15.5 14V16H14.5C13.4 16 12.5 16.9 12.5 18V28C12.5 29.1 13.4 30 14.5 30H26.5C27.6 30 28.5 29.1 28.5 28V18C28.5 16.9 27.6 16 26.5 16ZM20.5 25C19.4 25 18.5 24.1 18.5 23C18.5 21.9 19.4 21 20.5 21C21.6 21 22.5 21.9 22.5 23C22.5 24.1 21.6 25 20.5 25ZM17.5 16V14C17.5 12.34 18.84 11 20.5 11C22.16 11 23.5 12.34 23.5 14V16H17.5Z" fill="currentColor"/>
                </svg>
                <div class="wad4-video-details__player-overlay-text">
                  <a class="wad4-video-details__player-overlay-text--link" :href="protectedVideoSignupRedirectUrl(video)">Sign up</a>
                  or
                  <a class="wad4-video-details__player-overlay-text--link" :href="protectedVideoLoginRedirectUrl(video)"> log in</a>
                  to watch the video
                </div>
              </div>
              <VideoPlayer v-else ref="videoPlayerRef"
                           :key="video.id"
                           :title="video.title"
                           :posterUrl="videoPlaceholderImage"
                           :source="video.cloudflare_video_url"
                           :captionsUrl="captionsUrl"
                           :chaptersUrl="chaptersBlobUrl"
                           :autoPip="true"
                           :autoPlay="true"
                           @play-started="trackPlayInteraction"
                           @time-update="handleTimeUpdate"
              />
            </client-only>
          </article>
          <VideoTocCarousel v-if="video.sections?.length" :sections="video.sections" :onSeek="seekToTimestamp" :selectedIndex="highlightedIndex" :playback="playback"/>
        </div>
        <CommonJobsList v-if="videoRelatedJobs" class="wad4-video-details__jobs-list" :jobs="videoRelatedJobs" :skills="video.skills"/>
      </div>
    </main>
    <VideoMomentsCarousel class="wad4__container" :title="'Matching moments'" :moments="videoRecommendedMoments" />
    <section class="wad4__container">
      <h2 class="wad4__section-header wad4__section-header--small">
        <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.67 13.9523C18.04 14.8823 19 16.1423 19 17.8223V20.8223H23V17.8223C23 15.6423 19.43 14.3523 16.67 13.9523Z" fill="currentColor"/>
          <path d="M15 12.8223C17.21 12.8223 19 11.0323 19 8.82227C19 6.61227 17.21 4.82227 15 4.82227C14.53 4.82227 14.09 4.92227 13.67 5.06227C14.5 6.09227 15 7.40227 15 8.82227C15 10.2423 14.5 11.5523 13.67 12.5823C14.09 12.7223 14.53 12.8223 15 12.8223Z" fill="currentColor"/>
          <path d="M9 12.8223C11.21 12.8223 13 11.0323 13 8.82227C13 6.61227 11.21 4.82227 9 4.82227C6.79 4.82227 5 6.61227 5 8.82227C5 11.0323 6.79 12.8223 9 12.8223ZM9 6.82227C10.1 6.82227 11 7.72227 11 8.82227C11 9.92227 10.1 10.8223 9 10.8223C7.9 10.8223 7 9.92227 7 8.82227C7 7.72227 7.9 6.82227 9 6.82227Z" fill="currentColor"/>
          <path d="M9 13.8223C6.33 13.8223 1 15.1623 1 17.8223V20.8223H17V17.8223C17 15.1623 11.67 13.8223 9 13.8223ZM15 18.8223H3V17.8323C3.2 17.1123 6.3 15.8223 9 15.8223C11.7 15.8223 14.8 17.1123 15 17.8223V18.8223Z" fill="currentColor"/>
        </svg>
        <span>Featured Partners</span>
      </h2>
      <CommonCommunityPartnerAds class="wad4-video-details__ads"/>
    </section>
    <section class="wad4__container">
      <VideoCarousel :videos="videoRecommendedVideos" title="Related Videos"/>
    </section>
    <section class="wad4__container">
      <h2 class="wad4__section-header">From learning to earning</h2>
      <p class="wad4__section-subtitle">Jobs that call for the skills explored in this talk.</p>
      <div class="wad4-video-details__jobs wad4__container--columns">
          <JobCard class="wad4-video-details__jobs-card" v-for="job in videoRecommendedJobs" :key="job.id" :job="job"/>
      </div>
    </section>
  </div>
</template>

<style lang="scss">

.wad4-video-details {

  &__main {
    align-items: flex-start;
    row-gap: 0;

    &-header {
      display: flex;
      gap: $wad4-spacing-lg;
      flex-wrap: wrap;
      flex-direction: column;
      align-items: stretch;
      width: 100%;

      @include mqBreakPoint($wad4-breakpoint-tablet-max) {
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-end;
      }

      @include mqBreakPoint($wad4-breakpoint-desktop-min) {
        margin-right: 295px;
      }

      &-left {
        display: flex;
        flex-direction: column;
        gap: $wad4-spacing-xl;
        flex: 1;
        max-width: 800px;
      }

      &-right {
        flex-shrink: 0;
        display: flex;
        align-items: flex-start;

        @include mqBreakPoint($wad4-breakpoint-tablet-min) {
          width: auto;
          justify-content: flex-end;
        }
      }
    }
  }

  &__info {
    display: flex;
    gap: $wad4-spacing-md;
    color: $wad4-c-text-text-primary;

    &-icon {
      display: flex;
      width: 3rem;
      height: 3rem;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;
      border-radius: $wad4-border-radius-full;
      background: $wad4-c-background-bg-tertiery;
      color: $wad4-c-foreground-fg-brand-secondary;
    }

    &-data {
      display: flex;
      flex-direction: column;
      gap: $wad4-spacing-xs;
    }

    &-speaker {
      @include text-size-mixin('md');
      font-weight: $wad4-font-weight-semi-bold;
      margin: 0;
    }
  }

  &__title {
    @include display-size-mixin('xs');
    font-weight: $wad4-font-weight-regular-bold;
    margin: 0;
    font-family: $wad4-default-font-unbounded;
    color: $wad4-c-text-text-primary;
  }

  &__teaser {
    @include text-size-mixin('md');
    color: $wad4-c-text-text-primary;
    margin: 0;
  }

  &__jobs-list {
    background: linear-gradient(90deg, $wad4-c-background-bg-secondary 0%, $wad4-c-background-bg-primary 100%);

    @include mqBreakPoint($wad4-breakpoint-tablet-max) {
      background: none;
      width: 280px;
      padding-left: $wad4-spacing-lg;
    }
  }

  &__media-row {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-xl;
    width: 100%;
    padding-top: $wad4-col-gap;

    @include mqBreakPoint($wad4-breakpoint-desktop-min) {
      flex-direction: row;
      align-items: stretch;
    }
  }

  &__media-main{
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
  }

  &__player {
    width: 100%;
    aspect-ratio: 16/9;
    position: relative;
    border-radius: $wad4-border-radius-lg;

    &-image {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: auto;
      aspect-ratio: 16/9;
      border-radius: $wad4-border-radius-lg;
    }

    &-overlay {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: $wad4-spacing-xl;
      border-radius: $wad4-border-radius-lg;

      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: 3;
      background-color: $wad4-c-background-bg-brand-code100;

      &-text {
        color: $wad4-c-text-text-white;
        font-size: 16px;
        line-height: 24px;
        font-weight: 700;
        font-family: $wad4-default-font-mono;
        text-align: center;
        padding-left: $wad4-spacing-2xl;
        padding-right: $wad4-spacing-2xl;

        &--link {
          text-decoration: underline;
          text-underline-offset: 2px;
          text-decoration-skip-ink: none;
          cursor: pointer;
          color: $wad4-c-text-text-white;
        }
      }
    }

    &-media {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }
  }

  &__summary {
    text-align: left;
    display: block;
    border-radius: $wad4-border-radius-md;
    padding: 0 $wad4-container-spacing 0 0;
    color: $wad4-c-text-text-brand-secondary;
    overflow-y: auto;
    height: 192px;
    padding-bottom: 60px;
    @include text-size-mixin('md');

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: $wad4-c-background-bg-brand-tertiary;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background: $wad4-c-foreground-fg-brand-solid;
      border-radius: 10px;
    }

    &-container {
      overflow: hidden;
      max-height: 256px;
      min-height: 256px;
      padding-bottom: 16px;

      @include mqBreakPoint($wad4-breakpoint-desktop-min) {
        max-height: 264px;
        min-height: 264px;
      }
    }

    &-outer {
      position: relative;
      padding: $wad4-container-spacing;
      border-radius: $wad4-border-radius-md;
      background: $wad4-c-background-bg-brand-primary;
    }

    &-inner {
      position: relative;
      max-height: 240px;
      overflow: hidden;
    }

    p {
      white-space: pre-wrap;
      text-align: left;
      margin: 0 0 10px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    code {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
      background-color: var(--color-background-summary-code);
      color: var(--color-text-summary-code);
      padding: 0.2em 0.4em;
      border-radius: 4px;
      font-size: 14px;
    }

    &-gradient-overlay {
      position: absolute;
      bottom: 10px;
      left: 10px;
      right: 20px;
      height: 50px;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0), $wad4-c-background-bg-brand-primary);
      pointer-events: none;
      @include mqBreakPoint($wad4-breakpoint-tablet-min) {
        bottom: 20px;
        left: 20px;
        right: 30px;
        height: 50px;
      }
      @include mqBreakPoint($wad4-breakpoint-desktop-min) {
        bottom: 23px;
        left: 20px;
        right: 30px;
        height: 50px;
      }
    }
  }

  &__ads {
    margin-top: $wad4-spacing-3xl;
  }

  &__jobs {
    margin-top: $wad4-spacing-3xl;

    &-card {
      width: 100%;

      @include mqBreakPoint($wad4-breakpoint-tablet-min) {
        width: $wad4-col-4;
      }
    }
  }


  //region Transitions

  .summary-fade-slide-enter-active,
  .summary-fade-slide-leave-active {
    transition: all 0.7s ease-in-out;
  }

  .summary-fade-slide-enter-from,
  .summary-fade-slide-leave-to {
    opacity: 0;
    min-height: 0;
    max-height: 0;
    padding-bottom: 0;
    overflow: hidden;
  }

  .summary-fade-slide-leave-from,
  .summary-fade-slide-enter-to {
    opacity: 1;
    min-height: 256px;
    max-height: 256px;
    padding-bottom: 16px;

    @include mqBreakPoint($wad4-breakpoint-desktop-min) {
      min-height: 264px;
      max-height: 264px;
    }
  }

  //endregion
}
</style>
